/**
 * DataResolver - Resolves data bindings in template values
 * 
 * Handles resolution of:
 * - sectionData.* bindings (e.g., "sectionData.title")
 * - brandColor.* bindings (e.g., "brandColor.deepNavy")
 * - Map item bindings (e.g., "item.description" when inside a map)
 * - Nested paths (e.g., "sectionData.backgroundImage.desktop")
 */

import { getBrandColor, brandColors } from '@/config/brand-colors';
import { TemplateContext, SectionData, TemplateStyles, BindableValue } from '@/types/template-schema';

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if a value is a data binding string
 */
export function isDataBinding(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  
  // Check for common binding prefixes
  return (
    value.startsWith('sectionData.') ||
    value.startsWith('brandColor.') ||
    // Check for map item references (single word followed by dot)
    /^[a-z][a-zA-Z0-9]*\.[a-zA-Z]/.test(value)
  );
}

/**
 * Check if a value looks like it should be resolved (might be a binding)
 */
export function mightBeBinding(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  // Contains a dot and doesn't look like a URL or file path
  return value.includes('.') && 
    !value.startsWith('http') && 
    !value.startsWith('/') &&
    !value.startsWith('#') &&
    !value.includes(' ') &&
    !value.endsWith('.ts') &&
    !value.endsWith('.tsx') &&
    !value.endsWith('.js') &&
    !value.endsWith('.css');
}

// ============================================================================
// Path Resolution
// ============================================================================

/**
 * Get a value from a nested object using dot notation
 * @param obj The object to traverse
 * @param path The dot-notation path (e.g., "user.address.city")
 * @returns The value at the path, or undefined if not found
 */
export function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined;
  
  const parts = path.split('.');
  let current: unknown = obj;
  
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  
  return current;
}

/**
 * Set a value in a nested object using dot notation
 * Creates intermediate objects if they don't exist
 */
export function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let current = obj;
  
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!(part in current) || typeof current[part] !== 'object') {
      current[part] = {};
    }
    current = current[part] as Record<string, unknown>;
  }
  
  current[parts[parts.length - 1]] = value;
}

// ============================================================================
// Binding Resolution
// ============================================================================

/**
 * Resolve a brand color binding
 */
function resolveBrandColor(colorKey: string): string | undefined {
  const key = colorKey as keyof typeof brandColors;
  if (key in brandColors) {
    return getBrandColor(key);
  }
  return undefined;
}

/**
 * Resolve a section data binding
 */
function resolveSectionData(path: string, sectionData: SectionData | undefined): unknown {
  if (!sectionData) return undefined;
  return getNestedValue(sectionData, path);
}

/**
 * Resolve a map item binding
 */
function resolveMapItem(
  path: string, 
  context: TemplateContext
): unknown {
  // Check if we're inside a map and the path starts with the map variable
  if (context.currentItemAs && path.startsWith(context.currentItemAs + '.')) {
    const itemPath = path.substring(context.currentItemAs.length + 1);
    return getNestedValue(context.currentItem, itemPath);
  }
  
  // Direct map variable reference (e.g., just "item" not "item.something")
  if (context.currentItemAs && path === context.currentItemAs) {
    return context.currentItem;
  }
  
  return undefined;
}

/**
 * Main resolver function - resolves a single value
 * @param value The value to resolve (may be a binding string or literal)
 * @param context The template rendering context
 * @returns The resolved value
 */
export function resolveValue(
  value: unknown,
  context: TemplateContext
): unknown {
  // Non-string values pass through unchanged
  if (typeof value !== 'string') return value;
  
  // Handle special context properties that don't have dots (like currentIndex)
  if (value === 'currentIndex' && context.currentIndex !== undefined) {
    return context.currentIndex;
  }
  
  // Handle brand color bindings
  if (value.startsWith('brandColor.')) {
    const colorKey = value.substring('brandColor.'.length);
    const resolved = resolveBrandColor(colorKey);
    return resolved !== undefined ? resolved : value;
  }
  
  // Handle section data bindings
  if (value.startsWith('sectionData.')) {
    const path = value.substring('sectionData.'.length);
    const resolved = resolveSectionData(path, context.sectionData);
    return resolved !== undefined ? resolved : undefined;
  }
  
  // Handle map item bindings
  if (context.currentItemAs) {
    const resolved = resolveMapItem(value, context);
    if (resolved !== undefined) return resolved;
  }
  
  // Check if it might be a binding we should try to resolve
  if (mightBeBinding(value)) {
    // Try to resolve from context
    const resolved = getNestedValue(context, value);
    if (resolved !== undefined) return resolved;
  }
  
  // Return original value if no binding matched
  return value;
}

// ============================================================================
// String Interpolation (moved up for use in resolvePropValue)
// ============================================================================

/**
 * Resolve a string that might contain template literals
 * Supports ${binding} syntax within strings
 */
function resolveInterpolatedStringInternal(
  template: string,
  context: TemplateContext
): string {
  const pattern = /\$\{([^}]+)\}/g;
  return template.replace(pattern, (match, binding) => {
    const resolved = resolveValue(binding.trim(), context);
    if (resolved === undefined || resolved === null) return '';
    
    // Handle responsive image objects - extract URL instead of stringifying
    if (typeof resolved === 'object' && !Array.isArray(resolved)) {
      if ('desktop' in resolved || 'mobile' in resolved) {
        const imgObj = resolved as { desktop?: string; mobile?: string };
        return imgObj.desktop || imgObj.mobile || '';
      }
      if ('url' in resolved && typeof (resolved as { url: unknown }).url === 'string') {
        return (resolved as { url: string }).url;
      }
      if ('src' in resolved && typeof (resolved as { src: unknown }).src === 'string') {
        return (resolved as { src: string }).src;
      }
    }
    
    return String(resolved);
  });
}

/**
 * Remove quotes from string literals (handles both single and double quotes)
 */
function unquoteString(value: string): string {
  const trimmed = value.trim();
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
      (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

/**
 * Resolve a binding expression that may contain || fallback operators
 * e.g., "sectionData.backgroundMedia || sectionData.backgroundImage"
 */
function resolveBindingWithFallbacks(
  binding: string,
  context: TemplateContext
): unknown {
  // Check if binding contains || operators
  if (binding.includes('||')) {
    const parts = binding.split('||').map(p => p.trim());
    for (const part of parts) {
      const resolved = resolveValue(part, context);
      // Return first truthy value
      if (resolved !== undefined && resolved !== null && resolved !== '') {
        // If resolved value is the same as the part (meaning it's a literal string),
        // remove quotes if present
        if (typeof resolved === 'string' && resolved === part) {
          return unquoteString(resolved);
        }
        return resolved;
      }
    }
    // If all parts failed, try the last part as a literal (might be a quoted fallback)
    const lastPart = parts[parts.length - 1];
    if (lastPart && (lastPart.startsWith('"') || lastPart.startsWith("'"))) {
      return unquoteString(lastPart);
    }
    return undefined;
  }
  const resolved = resolveValue(binding.trim(), context);
  // If resolved value is the same as binding (meaning it's a literal string),
  // remove quotes if present
  if (typeof resolved === 'string' && resolved === binding.trim()) {
    return unquoteString(resolved);
  }
  return resolved;
}

/**
 * Resolve a string that might contain {{binding}} template literals
 * Supports {{binding}} syntax within strings (mustache-style)
 * Also supports || fallback operators: {{a || b || c}}
 */
function resolveMustacheStringInternal(
  template: string,
  context: TemplateContext
): string {
  const pattern = /\{\{([^}]+)\}\}/g;
  return template.replace(pattern, (match, binding) => {
    const resolved = resolveBindingWithFallbacks(binding.trim(), context);
    if (resolved === undefined || resolved === null) return '';
    
    // Handle responsive image objects - extract URL instead of stringifying
    if (typeof resolved === 'object' && !Array.isArray(resolved)) {
      if ('desktop' in resolved || 'mobile' in resolved) {
        const imgObj = resolved as { desktop?: string; mobile?: string };
        return imgObj.desktop || imgObj.mobile || '';
      }
      if ('url' in resolved && typeof (resolved as { url: unknown }).url === 'string') {
        return (resolved as { url: string }).url;
      }
      if ('src' in resolved && typeof (resolved as { src: unknown }).src === 'string') {
        return (resolved as { src: string }).src;
      }
    }
    
    return String(resolved);
  });
}

/**
 * Resolve a single prop value, handling mustache bindings
 * For prop values that are pure bindings (e.g., "{{sectionData.backgroundMedia}}"),
 * preserve responsive objects instead of converting to strings
 */
function resolvePropValue(value: unknown, context: TemplateContext): unknown {
  if (typeof value !== 'string') return value;
  
  // Check if the entire value is a mustache binding (not interpolated)
  const pureMustacheMatch = value.match(/^\{\{([^}]+)\}\}$/);
  if (pureMustacheMatch) {
    const binding = pureMustacheMatch[1].trim();
    const resolved = resolveBindingWithFallbacks(binding, context);
    
    // Preserve arrays for components that accept array props (like HeroCarousel banners)
    if (Array.isArray(resolved)) {
      return resolved;
    }
    
    // Preserve responsive objects for components that can handle them (like S3FilePreview)
    if (typeof resolved === 'object' && resolved !== null) {
      if ('desktop' in resolved || 'mobile' in resolved) {
        return resolved; // Preserve the object
      }
      // Preserve other objects as-is (for complex props)
      return resolved;
    }
    // For other values, convert to string if needed
    if (resolved === undefined || resolved === null) return '';
    return typeof resolved === 'string' ? resolved : String(resolved);
  }
  
  // Check for mustache-style bindings {{...}} within strings (interpolated)
  if (value.includes('{{')) {
    return resolveMustacheStringInternal(value, context);
  }
  
  // Check for ${...} bindings
  if (value.includes('${')) {
    return resolveInterpolatedStringInternal(value, context);
  }
  
  // Try direct binding resolution
  const resolved = resolveValue(value, context);
  // Preserve responsive objects for direct bindings too
  if (typeof resolved === 'object' && resolved !== null && !Array.isArray(resolved)) {
    if ('desktop' in resolved || 'mobile' in resolved) {
      return resolved; // Preserve the object
    }
  }
  return resolved;
}

/**
 * Resolve all values in a props object
 */
export function resolveProps(
  props: Record<string, unknown> | undefined,
  context: TemplateContext
): Record<string, unknown> | undefined {
  if (!props) return undefined;
  
  const resolved: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(props)) {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Recursively resolve nested objects
      resolved[key] = resolveProps(value as Record<string, unknown>, context);
    } else if (Array.isArray(value)) {
      // Resolve array items
      resolved[key] = value.map(item => 
        typeof item === 'object' && item !== null 
          ? resolveProps(item as Record<string, unknown>, context)
          : resolvePropValue(item, context)
      );
    } else {
      resolved[key] = resolvePropValue(value, context);
    }
  }
  
  return resolved;
}

/**
 * Resolve all values in a styles object
 */
export function resolveStyles(
  styles: TemplateStyles | undefined,
  context: TemplateContext
): React.CSSProperties | undefined {
  if (!styles) return undefined;
  
  const resolved: React.CSSProperties = {};
  
  for (const [key, value] of Object.entries(styles)) {
    // Use resolvePropValue instead of resolveValue to handle mustache bindings {{}}
    const resolvedValue = resolvePropValue(value, context);
    if (resolvedValue !== undefined) {
      (resolved as Record<string, unknown>)[key] = resolvedValue;
    }
  }
  return resolved;
}

/**
 * Evaluate a conditional expression
 * Returns true if the resolved value is truthy
 * Supports || fallback operators: "a || b || c"
 */
export function evaluateCondition(
  condition: string,
  context: TemplateContext
): boolean {
  const resolved = resolveBindingWithFallbacks(condition, context);
  return Boolean(resolved);
}

/**
 * Resolve an array for map iteration
 */
export function resolveArray(
  from: string,
  context: TemplateContext
): unknown[] {
  const resolved = resolveValue(from, context);
  
  if (Array.isArray(resolved)) {
    return resolved;
  }
  
  return [];
}

/**
 * Create a new context for map iteration
 */
export function createMapItemContext(
  parentContext: TemplateContext,
  item: unknown,
  itemAs: string,
  index: number
): TemplateContext {
  return {
    ...parentContext,
    currentItem: item,
    currentItemAs: itemAs,
    currentIndex: index,
    [itemAs]: item, // Also add as direct context property
  };
}

// ============================================================================
// Exported String Interpolation Functions (wrappers around internal functions)
// ============================================================================

/**
 * Resolve a string that might contain template literals
 * Supports ${binding} syntax within strings
 */
export const resolveInterpolatedString = resolveInterpolatedStringInternal;

/**
 * Resolve a string that might contain {{binding}} template literals
 * Supports {{binding}} syntax within strings (mustache-style)
 */
export const resolveMustacheString = resolveMustacheStringInternal;

/**
 * Resolve children content which may be strings, numbers, or binding references
 */
export function resolveChildContent(
  content: unknown,
  context: TemplateContext
): unknown {
  if (typeof content === 'string') {
    // Check if entire string is a binding (e.g., "sectionData.title")
    const resolved = resolveValue(content, context);
    if (resolved !== content) return resolved;
    
    // Check for mustache-style interpolated strings {{binding}}
    if (content.includes('{{')) {
      return resolveMustacheStringInternal(content, context);
    }
    
    // Check for JS-style interpolated strings ${binding}
    if (content.includes('${')) {
      return resolveInterpolatedStringInternal(content, context);
    }
    
    return content;
  }
  
  return content;
}

// ============================================================================
// Exports
// ============================================================================

export const DataResolver = {
  resolveValue,
  resolveProps,
  resolveStyles,
  evaluateCondition,
  resolveArray,
  createMapItemContext,
  resolveInterpolatedString,
  resolveMustacheString,
  resolveChildContent,
  getNestedValue,
  setNestedValue,
  isDataBinding,
  mightBeBinding,
};

export default DataResolver;



















